import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { ClerkProvider } from '@clerk/react';
import { registerSW } from 'virtual:pwa-register';

import App from './App';
import { clerkPublishableKey, isClerkEnabled } from './config/clerk';
import { setupErrorTracking } from './services/analytics';
import { ThemeProvider } from './contexts/ThemeContext';
import './index.css';

const scheduleNonCriticalBootTask = (task) => {
  if (typeof window === 'undefined') {
    return;
  }

  const queueTask = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => task(), { timeout: 1500 });
      return;
    }

    window.setTimeout(task, 0);
  };

  if (document.readyState === 'complete') {
    queueTask();
    return;
  }

  window.addEventListener('load', queueTask, { once: true });
};

if (import.meta.env.VITE_SENTRY_DSN_CLIENT) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN_CLIENT,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_RELEASE || import.meta.env.VERCEL_GIT_COMMIT_SHA || 'dev',
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  });
}

// Keep analytics and service worker setup off the first paint path so navigation feels immediate.
scheduleNonCriticalBootTask(() => {
  setupErrorTracking();
  registerSW();
});

const appTree = (
  <ThemeProvider storageKey="oakley-client-theme">
    <App />
  </ThemeProvider>
);

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<div style={{ padding: '2rem', textAlign: 'center' }}>Something went wrong. Please refresh the page.</div>}>
      {isClerkEnabled ? (
        <ClerkProvider publishableKey={clerkPublishableKey}>
          {appTree}
        </ClerkProvider>
      ) : (
        appTree
      )}
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
